import type { KeyboardEvent } from "react";
import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import clsx from "clsx";

import classes from "./styles.module.scss";

import { Input } from "@components/formControls/input";
import type { IPhoneProps, IValidation } from "@components/formControls";
import { SearchInput } from "@components/formControls/searchInput";
import { Menu } from "@/components/atoms/menu";
import { MenuItem } from "@/components/atoms/menuItem";
import { Button } from "@/components/atoms/button";
import type { IOption } from "@/interfaces";
import { MenuContent, MenuHeader } from "@/components/atoms/menu/menu";
import { useReadOnly } from "@/hooks";
import { Flag } from "@/components/atoms/flags";
import { formatNumber, getDefaultPhoneCode, isPhoneValid } from "@/utils";

const DEFAULT_CODE = getDefaultPhoneCode();

function getPhoneValidation(
  isValid: boolean | undefined,
): IValidation | undefined {
  if (isValid === undefined) return undefined;

  return isValid ? "success" : "warning";
}

export const Phone = memo(function Phone(props: IPhoneProps) {
  const {
    value = { code: DEFAULT_CODE, number: "" },
    onChangeValue,
    onFocus,
    onBlur,
    countries,
    className,
    disabled,
    readOnly: propsReadOnly,
    defaultCode = DEFAULT_CODE,
    validation: propsValidation,
    ...rest
  } = props;

  const readOnly = useReadOnly(propsReadOnly);
  // input ref
  const inputRef = useRef<HTMLInputElement>(null);

  // code button ref
  const buttonRef = useRef<HTMLButtonElement>(null);

  // search input ref
  const searchInputRef = useRef<HTMLInputElement>(null);

  // state for store the select country code with its flag url
  const [selectedFlag, setSelectedFlag] = useState<string>();

  // state for store search input value
  const [searchInputValue, setSearchInputValue] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const validation: IValidation | undefined = isInputFocused
    ? getPhoneValidation(isPhoneValid(value))
    : propsValidation;

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  const openHandler = useCallback(
    (value: boolean) => {
      if (!readOnly && !disabled) {
        setIsOpen(value);
      }
    },
    [disabled, readOnly],
  );

  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: openHandler,
    whileElementsMounted: autoUpdate,
    middleware: [
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${240}px`,
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const listRef = useRef<(HTMLElement | null)[]>([]);

  const dismiss = useDismiss(context, {
    outsidePress(event) {
      const target = event.target as HTMLElement;

      return !buttonRef.current?.contains(target);
    },
  });
  const role = useRole(context, { role: "listbox" });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [role, dismiss, listNav],
  );

  const items = countries.filter((item) =>
    item.value.toLowerCase().startsWith(searchInputValue.toLowerCase()),
  );

  function inputChangeHandler(_value: string) {
    // Remove non-numeric characters
    const numericValue = _value.replaceAll(/[^\d۰-۹]/g, "");
    const trimmedNumericValue = numericValue.slice(0, 10);

    onChangeValue?.({
      code: value.code,
      number: formatNumber({ code: value.code, number: trimmedNumericValue }),
    });
  }

  function selectHandler(item?: IOption<string>) {
    let selectedItem = item;

    if (!selectedItem && activeIndex != null && items[activeIndex]) {
      selectedItem = items[activeIndex];
    }
    if (selectedItem) {
      setSelectedFlag(selectedItem.flag);
      onChangeValue?.({ code: selectedItem.key, number: value.number });
      setActiveIndex(null);
      openHandler(false);
      inputRef.current?.focus();
    }
  }

  function searchInputKeyDownHandler(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      // prevent form submitting
      event.preventDefault();
      selectHandler();
    }
  }

  return (
    <>
      <div
        className={classes.container}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <Input
          validation={validation}
          ref={inputRef}
          dir={"ltr"}
          inputType={"tel"}
          className={clsx([classes.phoneInput, className])}
          value={value.number}
          onChangeValue={inputChangeHandler}
          readOnly={readOnly}
          disabled={disabled}
          onFocus={(event) => {
            setIsInputFocused(true);
            openHandler(false);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            onBlur?.(event);
            setIsInputFocused(false);
          }}
          {...rest}
          placeholder={getPlaceholder(value.code || "+98")}
        />
        <Button
          ref={buttonRef}
          className={classes.codeContainer}
          onClick={() => {
            openHandler(!isOpen);
          }}
          mode={"select"}
        >
          <Flag
            country={selectedFlag || (value.code === "+98" ? "ir" : "iq")}
          />
          <div>{value.code || defaultCode}</div>
        </Button>
      </div>
      <FloatingPortal>
        {isOpen && (
          <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={-1}
            visuallyHiddenDismiss
          >
            <Menu
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
              }}
              {...getFloatingProps()}
            >
              <MenuHeader>
                <Suspense>
                  <SearchInput
                    value={searchInputValue}
                    onChangeValue={setSearchInputValue}
                    autoComplete={"off"}
                    ref={searchInputRef}
                    onKeyDown={searchInputKeyDownHandler}
                  />
                </Suspense>
              </MenuHeader>
              <MenuContent>
                {items.map((item, index) => (
                  <MenuItem
                    key={item.key || item.value}
                    {...getItemProps({
                      ref(node) {
                        listRef.current[index] = node;
                      },
                      onClick() {
                        selectHandler(item);
                      },
                    })}
                    active={activeIndex === index}
                  >
                    <Flag country={item.flag!} />
                    <div>{item.value}</div>
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  );
});

const getPlaceholder = (code: string) => {
  if (code === "+964") {
    return "xxx xxx xxxx";
  }
  return "xxx xxx xxxx";
};
